import { useQuery } from '@tanstack/react-query';

export const DB_NAME = 'itemFormDB';
export const STORE_NAME = 'images';

export const openDB = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject('Failed to open DB');
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });

export const saveImageToDB = async (key: string, base64: string) => {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  store.put(base64, key);
};

export const replaceAllImagesFromDB = async (images: string[]) => {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  store.clear();
  images.forEach(async (image, index) => {
    store.put(image, `item-image-${index}`);
  });
};

export const getAllImagesFromDB = async (): Promise<string[]> => {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onerror = () => reject('Failed to get images');
    request.onsuccess = () => resolve(request.result);
  });
};

export const clearAllImagesFromDB = async () => {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).clear();
  return tx.oncomplete;
};

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, _) => {
    if (blob instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    } else {
      resolve(blob as string);
    }
  });
}

export const clearAndSaveAllImages = async (images: string[]) => {
  await clearAllImagesFromDB();
  await replaceAllImagesFromDB(images);
};

export const useFetchSavedImages = () => {
  return useQuery({
    queryKey: ['saved-image'],
    queryFn: getAllImagesFromDB,
    staleTime: 0,
  });
};
